import _ from 'lodash';
import { PayoutTypes } from './PayoutDetails';
import { PaymentProviderIdTypes } from './PaymentProviderIdTypes';
import { MoneyMovementOperationTypes } from './MoneyMovementOperationTypes';

export type TPaymentsConfig = {
  providers: {
    // eslint-disable-next-line no-restricted-globals
    name: string;
    id: PaymentProviderIdTypes;
    operationType: MoneyMovementOperationTypes;
    isOnrampMethod: boolean;
    enabled: boolean;
    batchAvailable: boolean;
    onboardingType: 'customer-service';
    availableDepositCurrencies?: ('USD' | 'EUR')[];
    availableDepositNetworks?: ('Ethereum' | 'Polygon' | 'Base' | 'Solana')[];
    suitableForCurrencies: (
      | 'USDC'
      | 'USDT'
      | 'EUR'
      | 'USD'
      | 'USDC.native_polygon'
    )[];
    suitableForCryptoNetworks?: (
      | 'Polygon'
      | 'Ethereum'
      | 'Base'
      | 'Tron'
      | 'Solana'
    )[];
    depositRailsByCurrencies?: Record<
      'USD' | 'EUR',
      {
        value: 'ach_push' | 'wire' | 'local';
        label: string;
      }[]
    >;
    suitableForPayouts: string[];
  }[];
};

export const paymentsConfig: TPaymentsConfig = {
  providers: [
    {
      name: 'Pay with Fiat Currency',
      id: PaymentProviderIdTypes.BridgeOnramp,
      operationType: MoneyMovementOperationTypes.Onramp,
      isOnrampMethod: true,
      enabled: true,
      onboardingType: 'customer-service',
      batchAvailable: false,
      availableDepositCurrencies: ['USD', 'EUR'],
      depositRailsByCurrencies: {
        USD: [
          {
            value: 'ach_push',
            label: 'ACH (2-4 working days)',
          },
          {
            value: 'wire',
            label: 'WIRE (1-2 working days)',
          },
        ],
        EUR: [
          {
            value: 'local',
            label: 'SEPA',
          },
        ],
      },
      suitableForCurrencies: ['USDC', 'USDC.native_polygon'],
      suitableForCryptoNetworks: ['Polygon', 'Ethereum', 'Base'],
      suitableForPayouts: [PayoutTypes.CryptoTransfer],
    },
    {
      name: 'Settle With Crypto',
      id: PaymentProviderIdTypes.BridgeOfframp,
      operationType: MoneyMovementOperationTypes.Offramp,
      enabled: true,
      isOnrampMethod: false,
      onboardingType: 'customer-service',
      batchAvailable: false,
      suitableForCurrencies: ['EUR', 'USD'],
      availableDepositNetworks: ['Ethereum', 'Polygon', 'Base', 'Solana'],
      suitableForPayouts: [PayoutTypes.BankTransfer],
    },
    {
      name: 'Pay with Fiat Currency',
      id: PaymentProviderIdTypes.EasyPayOnramp,
      operationType: MoneyMovementOperationTypes.Onramp,
      isOnrampMethod: true,
      enabled: true,
      onboardingType: 'customer-service',
      depositRailsByCurrencies: {
        USD: [
          {
            value: 'ach_push',
            label: 'ACH (2-4 working days)',
          },
          {
            value: 'wire',
            label: 'WIRE (1-2 working days)',
          },
        ],
        EUR: [
          {
            value: 'local',
            label: 'SEPA',
          },
        ],
      },
      batchAvailable: false,
      availableDepositCurrencies: ['USD', 'EUR'],
      suitableForCurrencies: ['USDC', 'USDC.native_polygon'],
      suitableForCryptoNetworks: ['Polygon', 'Ethereum', 'Base'],
      suitableForPayouts: [PayoutTypes.CryptoTransfer],
    },
    {
      name: 'Pay with Fiat Currency',
      id: PaymentProviderIdTypes.RedEnvelopeOnramp,
      operationType: MoneyMovementOperationTypes.Onramp,
      enabled: true,
      isOnrampMethod: true,
      batchAvailable: false,
      onboardingType: 'customer-service',
      availableDepositCurrencies: ['USD'],
      suitableForCurrencies: ['USDC', 'USDT'],
      suitableForCryptoNetworks: ['Polygon', 'Ethereum'],
      suitableForPayouts: [PayoutTypes.CryptoTransfer],
    },
    {
      name: 'Pay with Crypto',
      id: PaymentProviderIdTypes.EasyPayCrossChainTransfer,
      enabled: true,
      operationType: MoneyMovementOperationTypes.CrossChainTransfer,
      isOnrampMethod: false,
      batchAvailable: false,
      onboardingType: 'customer-service',
      suitableForCurrencies: ['USDC', 'USDT', 'USDC.native_polygon'],
      suitableForCryptoNetworks: [
        'Polygon',
        'Ethereum',
        'Base',
        'Tron',
        'Solana',
      ],
      suitableForPayouts: [PayoutTypes.CryptoTransfer],
    },
    {
      name: 'Settle With Crypto',
      id: PaymentProviderIdTypes.EasyPayOfframpTransfer,
      operationType: MoneyMovementOperationTypes.Offramp,
      enabled: true,
      isOnrampMethod: false,
      onboardingType: 'customer-service',
      batchAvailable: false,
      suitableForCurrencies: ['EUR', 'USD'],
      availableDepositNetworks: ['Ethereum', 'Polygon', 'Base', 'Solana'],
      suitableForPayouts: [PayoutTypes.BankTransfer],
    },
  ],
};

export class PaymentsConfig {
  private readonly config: TPaymentsConfig;

  constructor(config: TPaymentsConfig) {
    this.config = config;
  }

  get(path: string, fallback: any) {
    return _.get(this.config, path, fallback);
  }
}

export const getTransferPaymentsConfig = () => {
  return new PaymentsConfig(paymentsConfig);
};
